import { csvParse } from "d3";
import { useCallback, useEffect, useMemo } from "react";
import { read, utils } from "xlsx";

import useFileImportStore from "../../../stores/fileImportStore";

function extractBOMsFromRows(rows) {
  const boms = [];
  const headers = Object.keys(rows[0]);
  const hasMultipleBOMs = headers.indexOf("BOM") !== -1;
  if (hasMultipleBOMs) {
    const bomIndicesInCsv = rows.map((row) => row.BOM).filter((bomIndex, currentIndex, array) => array.indexOf(bomIndex) === currentIndex);
    bomIndicesInCsv.forEach(() => boms.push([]));
    rows.forEach((row) => {
      const currentBomIndex = bomIndicesInCsv.indexOf(row.BOM);
      const rowCopy = { ...row };
      delete rowCopy.BOM;
      boms[currentBomIndex].push(rowCopy);
    });
  } else {
    boms.push([...rows]);
  }
  return boms;
}

export default function useProcessUploadedFiles() {
  const currentUnparsedBOM = useFileImportStore((s) => s.currentUnparsedBOM);
  const currentFile = useFileImportStore((s) => s.currentFile);

  const processCsvFile = useCallback(() => {
    const rows = csvParse(currentFile.data);
    const boms = extractBOMsFromRows(rows);
    useFileImportStore.getState().setUnParsedBOMs(boms);
  }, [currentFile]);

  const processExcelFile = useCallback(() => {
    fetch(currentFile.data)
      .then((response) => response.blob())
      .then((responseData) => {
        responseData.arrayBuffer().then((arrayBuffer) => {
          const workbook = read(arrayBuffer);
          let boms = [];
          if (workbook.SheetNames.length > 1) {
            workbook.SheetNames.forEach((sheetName) => {
              const metaData = workbook.Workbook.Sheets.find((sheetMetadata) => sheetMetadata.name === sheetName);
              if (metaData.Hidden === 0) {
                const sheet = workbook.Sheets[sheetName];
                const sheetJson = utils.sheet_to_json(sheet, { raw: false, defval: "" });
                const rows = sheetJson.map((sheetRow) => {
                  const sheetRowCopy = { ...sheetRow };
                  Object.keys(sheetRow).forEach((header) => {
                    if (header.startsWith("__")) {
                      delete sheetRowCopy[header];
                    }
                  });
                  return sheetRowCopy;
                });
                boms.push(rows);
              }
            });
          } else {
            const firstSheetName = workbook.SheetNames[0];
            const firstSheet = workbook.Sheets[firstSheetName];
            const firstSheetAsJson = utils.sheet_to_json(firstSheet, { raw: false, defval: "" });
            const rows = firstSheetAsJson.map((sheetRow) => {
              const sheetRowCopy = { ...sheetRow };
              Object.keys(sheetRow).forEach((header) => {
                if (header.startsWith("__")) {
                  delete sheetRowCopy[header];
                }
              });
              return sheetRowCopy;
            });
            boms = extractBOMsFromRows(rows);
          }
          useFileImportStore.getState().setUnParsedBOMs(boms);
        });
      });
  }, [currentFile]);

  const handleFiles = useCallback((files) => {
    const file = files[0];
    const fileType = file.type;
    const reader = new FileReader();
    reader.addEventListener("load", (e) => {
      useFileImportStore.getState().setCurrentFile({
        type: fileType,
        data: e.target.result,
        name: file.name,
      });
    });
    if (fileType === "text/csv" || fileType === "text/plain") {
      reader.readAsText(file);
    } else {
      reader.readAsDataURL(file);
    }
  }, []);

  useEffect(() => {
    if (currentFile && (currentFile.type === "text/csv" || currentFile.type === "text/plain") && processCsvFile) {
      processCsvFile();
    } else if (currentFile && processExcelFile) {
      processExcelFile();
    }
  }, [currentFile, processCsvFile, processExcelFile]);

  useEffect(() => {
    if (currentFile && currentUnparsedBOM) {
      useFileImportStore.getState().setUploadProgression({
        fileName: currentFile.name,
        percentComplete: 100,
        timeRemaining: "0s",
      });
    } else if (currentFile && !currentUnparsedBOM) {
      useFileImportStore.getState().setUploadProgression({
        fileName: currentFile.name,
        percentComplete: 50,
        timeRemaining: "30s",
      });
    }
  }, [currentUnparsedBOM, currentFile]);

  return useMemo(() => ({ handleFiles }), [handleFiles]);
}
